import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { BrandingProvider, useBrandingLoading, useBranding } from './contexts/BrandingContext';
import { CmsNavProvider, useCmsHomepageSlug, useCmsNavLoading } from './contexts/CmsNavContext';
import ProtectedRoute from './components/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { VerifyEmailPage } from './pages/VerifyEmailPage';
import { ProfilePage } from './pages/ProfilePage';
import { DashboardPage } from './pages/DashboardPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { MessagesPage } from './pages/MessagesPage';
import { LoyaltyPage } from './pages/LoyaltyPage';
import { CmsPage } from './pages/CmsPage';
import { LoyaltySidebarLayout } from './components/LoyaltySidebarLayout';
import { LoyaltyDashboardBlock } from './components/blocks/LoyaltyDashboardBlock';
import { LoyaltyBadgesBlock } from './components/blocks/LoyaltyBadgesBlock';
import { LoyaltyLeaderboardBlock } from './components/blocks/LoyaltyLeaderboardBlock';
import { LoyaltyProfileBlock } from './components/blocks/LoyaltyProfileBlock';
import { CmsPreviewPage } from './pages/CmsPreviewPage';
import { CmsRedirectHandler } from './pages/CmsRedirectHandler';
import { CollectionEntryPage } from './pages/CollectionEntryPage';
import { CollectionListPage } from './pages/CollectionListPage';
import { setTokenPair } from './lib/auth';

/**
 * Handles the OAuth redirect callback: after the server-side OAuth flow completes,
 * the Lambda redirects back to the portal with ?access_token=...&refresh_token=...
 * This component detects those params, stores the tokens, and navigates to /profile.
 */
function OAuthCallbackHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (accessToken && refreshToken) {
      // Parse expiresIn from JWT exp claim
      let expiresIn = 900;
      try {
        const [, payload] = accessToken.split('.');
        const decoded = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/'))) as {
          sub?: string;
          exp?: number;
          iat?: number;
        };
        if (decoded.exp && decoded.iat) {
          expiresIn = decoded.exp - decoded.iat;
        }
      } catch {
        /* use default */
      }

      setTokenPair({
        accessToken,
        refreshToken,
        expiresIn,
        customerId: '', // will be read from the JWT payload by getCurrentCustomer()
      });

      // Remove tokens from URL bar (security: don't leave them in history)
      const cleanUrl = window.location.pathname;
      window.history.replaceState({}, '', cleanUrl);

      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  return null;
}

/**
 * Handles the root route: if a CMS homepage is configured, renders that page;
 * otherwise falls back to /dashboard redirect.
 * Must be used inside CmsNavProvider so useCmsHomepageSlug is available.
 */
function HomeRoute() {
  const homepageSlug = useCmsHomepageSlug();
  const navLoading = useCmsNavLoading();

  // While nav is loading show a spinner (BrandingLoadingGate covers the very first paint,
  // but HomeRoute can be hit before the nav fetch completes on subsequent navigations)
  if (navLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <div className="w-6 h-6 rounded-full border-2 border-zinc-200 border-t-zinc-500 animate-spin" />
      </div>
    );
  }

  if (homepageSlug) {
    // Render the CMS page with slug injected directly (no URL param needed)
    return <CmsPage overrideSlug={homepageSlug} />;
  }

  return <Navigate to="/dashboard" replace />;
}

/**
 * Fullscreen skeleton shown while the tenant branding is being fetched.
 * Prevents a flash of the default indigo color before tenant colors are applied.
 */
function BrandingLoadingGate({ children }: { children: React.ReactNode }) {
  const isLoading = useBrandingLoading();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-zinc-200 animate-pulse" />
        <div className="space-y-2 flex flex-col items-center">
          <div className="h-3 w-32 rounded bg-zinc-200 animate-pulse" />
          <div className="h-2 w-24 rounded bg-zinc-100 animate-pulse" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function AppRoutes() {
  return (
    <>
      <OAuthCallbackHandler />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/messages"
          element={
            <ProtectedRoute>
              <MessagesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/loyalty"
          element={
            <ProtectedRoute>
              <LoyaltyPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/privacy"
          element={
            <ProtectedRoute>
              <PrivacyPage />
            </ProtectedRoute>
          }
        />
        {/* ── Loyalty direct routes (no CMS required) ───────────────── */}
        <Route
          path="/loyalty/dashboard"
          element={
            <LoyaltySidebarLayout>
              <LoyaltyDashboardBlock />
            </LoyaltySidebarLayout>
          }
        />
        <Route
          path="/loyalty/badges"
          element={
            <LoyaltySidebarLayout>
              <LoyaltyBadgesBlock />
            </LoyaltySidebarLayout>
          }
        />
        <Route
          path="/loyalty/leaderboard"
          element={
            <LoyaltySidebarLayout>
              <LoyaltyLeaderboardBlock />
            </LoyaltySidebarLayout>
          }
        />
        <Route
          path="/loyalty/profile"
          element={
            <LoyaltySidebarLayout>
              <LoyaltyProfileBlock />
            </LoyaltySidebarLayout>
          }
        />
        <Route path="/p/:slug" element={<CmsPage />} />
        <Route path="/c/:typeSlug/:slug" element={<CollectionEntryPage />} />
        <Route path="/c/:typeSlug" element={<CollectionListPage />} />
        <Route path="/preview/:token" element={<CmsPreviewPage />} />
        <Route path="/" element={<HomeRoute />} />
        <Route path="*" element={<CmsRedirectHandler />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <BrandingProvider>
      <BrowserRouter>
        <BrandingLoadingGate>
          <CmsNavProviderWithBranding />
        </BrandingLoadingGate>
      </BrowserRouter>
    </BrandingProvider>
  );
}

/**
 * Reads defaultSpaceId from BrandingContext and injects it into CmsNavProvider.
 * Must be rendered inside BrandingProvider.
 */
function CmsNavProviderWithBranding() {
  const branding = useBranding();
  return (
    <CmsNavProvider defaultSpaceId={branding.defaultSpaceId}>
      <AppRoutes />
    </CmsNavProvider>
  );
}
